import { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowLeftIcon, CloseIcon, ImageIcon, LoginIcon, PlusIcon, SearchIcon } from './Icons';
import type { Product } from '../types/api';
import { resolveApiUrl } from '../services/api';
import ProductImage from './ProductImage';
import './QuickSearch.css';

interface Props {
  onBack: () => void;
  isAuthenticated: boolean;
  onSearch: (query: string) => Promise<Product[]>;
  onLoginClick: () => void;
  onAddToCart?: (productId: string, productName: string, price: number) => void;
}

export default function QuickSearch({ onBack, isAuthenticated, onSearch, onLoginClick, onAddToCart }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const visibleResults = results.filter(item => item.active && item.available);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const data = await onSearch(q);
      setResults(data);
    } finally {
      setLoading(false);
    }
  }, [onSearch]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      doSearch(query);
    }, 300);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query, doSearch]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onBack();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onBack]);

  return (
    <div className="quick-search section-shell">
      <div className="quick-search-header surface-card">
        <button className="quick-search-back" onClick={onBack} aria-label="Voltar à vitrine" type="button">
          <ArrowLeftIcon size={20} />
        </button>
        <div className="quick-search-field">
          <SearchIcon size={18} />
          <input
            type="text"
            className="quick-search-input"
            placeholder="Digite código, nome ou referência..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoFocus
            aria-label="Busca rápida de produtos"
          />
          {query && (
            <button className="quick-search-clear" onClick={() => setQuery('')} aria-label="Limpar busca" type="button">
              <CloseIcon size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="quick-search-results">
        <div className="quick-search-meta">
          <strong>Consulta rápida</strong>
          <span>{visibleResults.length} resultado{visibleResults.length === 1 ? '' : 's'}</span>
        </div>

        {loading && <div className="quick-search-loading">Buscando produtos reais...</div>}

        {!loading && query && visibleResults.length === 0 && (
          <div className="quick-search-empty">
            <ImageIcon size={26} />
            <p>Nenhum produto encontrado para "<strong>{query}</strong>"</p>
            <span>Tente outra referência ou código.</span>
          </div>
        )}

        {visibleResults.map(item => (
          <div key={item.id} className="quick-search-item">
            <div className="quick-search-item-media">
              {item.image && !item.image.includes('product-placeholder.svg') && !imageErrors[item.id] ? (
                  <ProductImage
                    src={resolveApiUrl(item.image)}
                    alt={item.name}
                    className="quick-search-item-img"
                    onFinalError={() => setImageErrors(prev => ({ ...prev, [item.id]: true }))}
                  />
                ) : (
                <div className="quick-search-item-img quick-search-item-img--fallback" aria-hidden="true">
                  <ImageIcon size={20} />
                  <span>Sem foto</span>
                </div>
              )}
            </div>
            <div className="quick-search-item-info">
              <span className="quick-search-item-code">Ref. {item.code}</span>
              <p className="quick-search-item-name">{item.name}</p>
              <span className="quick-search-item-category">{item.category}</span>
            </div>
            <div className="quick-search-item-actions">
              {isAuthenticated && item.price != null ? (
                <span className="quick-search-item-price">
                  {item.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              ) : (
                <span className="quick-search-item-price-hidden">Preço disponível após login</span>
              )}
              <div className="quick-search-item-buttons">
                {!isAuthenticated ? (
                  <button className="quick-search-login-btn" onClick={onLoginClick} type="button">
                    <LoginIcon size={16} />
                    Entrar
                  </button>
                ) : (
                  <button
                    className="quick-search-add-btn"
                    disabled={!item.active || !item.available}
                    onClick={() => onAddToCart?.(item.id, item.name, item.price || 0)}
                    aria-label="Adicionar"
                    type="button"
                  >
                    <PlusIcon size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
